import React, { useState, useEffect, useRef } from 'react';
import { 
  Bold, Italic, Underline, Link, Code, Heading2, Heading3, Quote, List, ListOrdered, Image, 
  Eye, Edit, Columns, Save, X, Sparkles, Check, ArrowLeft, RefreshCw, 
  Trash2
} from 'lucide-react';
import { motion } from 'motion/react';

const PRESET_IMAGES = [
  {
    name: 'Misty Forest Canopy',
    url: 'https://images.unsplash.com/photo-1502082553048-f009c37129b9?auto=format&fit=crop&q=80&w=1200',
  },
  {
    name: 'Verdant Mountain Peak',
    url: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&q=80&w=1200',
  },
  {
    name: 'Green Leaf Dew drops',
    url: 'https://images.unsplash.com/photo-1500627869374-13cd993b1115?auto=format&fit=crop&q=80&w=1200',
  },
  {
    name: 'Rich Organic Soil & Sprout',
    url: 'https://images.unsplash.com/photo-1599599810769-bcde5a160d32?auto=format&fit=crop&q=80&w=1200',
  },
  {
    name: 'Quiet Golden Sunlight Field',
    url: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&q=80&w=1200',
  }
];

export default function BlogEditor({
  post,
  onSave,
  onCancel,
}) {
  const [title, setTitle] = useState(post?.title || '');
  const [subtitle, setSubtitle] = useState(post?.subtitle || '');
  const [content, setContent] = useState(post?.content || '');
  const [coverImage, setCoverImage] = useState(post?.coverImage || PRESET_IMAGES[0].url);
  const [tagsInput, setTagsInput] = useState((post?.tags || []).join(', ') || '');
  const [status, setStatus] = useState(post?.status || 'published');
  const [readTime, setReadTime] = useState(post?.readTime || '3 min read');

  const [editorTab, setEditorTab] = useState('edit');
  const [editorMode, setEditorMode] = useState('visual');
  const [isSavedNotify, setIsSavedNotify] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hoveredImgInfo, setHoveredImgInfo] = useState(null);
  const textareaRef = useRef(null);
  const visualEditorRef = useRef(null);
  const coverImageInputRef = useRef(null);
  const inlineImageInputRef = useRef(null);

  useEffect(() => {
    const cleanText = content.replace(/<[^>]*>/g, ''); 
    const wordCount = cleanText.trim().split(/\s+/).filter(Boolean).length;
    const minutes = Math.max(1, Math.ceil(wordCount / 180)); 
    setReadTime(`${minutes} min read`);
  }, [content]);

  useEffect(() => {
    if (editorMode === 'visual' && visualEditorRef.current) {
      if (document.activeElement !== visualEditorRef.current) {
        visualEditorRef.current.innerHTML = content || '';
      }
    }
  }, [editorMode, content]);

  const handleVisualInput = () => {
    if (visualEditorRef.current) {
      setContent(visualEditorRef.current.innerHTML);
    }
  };

  const handleCoverImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      setCoverImage(event.target.result);
    };
    reader.readAsDataURL(file);
    e.target.value = null;
  };

  const handleInlineImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target.result;
      if (editorMode === 'visual') {
        if (visualEditorRef.current) {
          visualEditorRef.current.focus();
        }
        document.execCommand('insertHTML', false, `<img src="${base64}" alt="Uploaded Image" class="w-full h-auto rounded-xl my-6" />`);
        if (visualEditorRef.current) {
          setContent(visualEditorRef.current.innerHTML);
        }
      } else {
        injectTag('imageUpload', base64);
      }
    };
    reader.readAsDataURL(file);
    e.target.value = null;
  };

  const handleFormat = (type, value = null) => {
    if (editorMode === 'visual') {
      if (visualEditorRef.current) {
        visualEditorRef.current.focus();
      }
      switch (type) {
        case 'fontName':
          if (value) document.execCommand('fontName', false, value);
          break;
        case 'fontSize':
          if (value) document.execCommand('fontSize', false, value);
          break;
        case 'bold':
          document.execCommand('bold', false);
          break;
        case 'italic':
          document.execCommand('italic', false);
          break;
        case 'underline':
          document.execCommand('underline', false);
          break;
        case 'h2':
          if (document.queryCommandValue('formatBlock') === 'h2') {
            document.execCommand('formatBlock', false, '<p>');
          } else {
            document.execCommand('formatBlock', false, '<h2>');
          }
          break;
        case 'h3':
          if (document.queryCommandValue('formatBlock') === 'h3') {
            document.execCommand('formatBlock', false, '<p>');
          } else {
            document.execCommand('formatBlock', false, '<h3>');
          }
          break;
        case 'blockquote':
          if (document.queryCommandValue('formatBlock') === 'blockquote') {
            document.execCommand('formatBlock', false, '<p>');
          } else {
            document.execCommand('formatBlock', false, '<blockquote>');
          }
          break;
        case 'ul':
          document.execCommand('insertUnorderedList', false);
          break;
        case 'ol':
          document.execCommand('insertOrderedList', false);
          break;
        case 'link':
          const href = window.prompt('Enter website link URL:', 'https://');
          if (href) {
            document.execCommand('createLink', false, href);
          }
          break;
        case 'image':
          const imgUrl = window.prompt('Enter image URL:', 'https://images.unsplash.com/photo-1447752875215-b2761acb3c5d?auto=format&fit=crop&q=80&w=800');
          if (imgUrl) {
            document.execCommand('insertHTML', false, `<img src="${imgUrl}" alt="Scenic Image" class="w-full h-auto rounded-xl my-6" referrerPolicy="no-referrer" />`);
          }
          break;
        case 'uploadImage':
          inlineImageInputRef.current?.click();
          break;
        default:
          break;
      }
      if (visualEditorRef.current) {
        setContent(visualEditorRef.current.innerHTML);
      }
    } else {
      injectTag(type, value);
    }
  };

  const injectTag = (tagType, value = null) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value;
    const selectedText = text.substring(start, end);

    let replacement = '';
    switch (tagType) {
      case 'fontName':
        if (!value) return;
        replacement = `<span style="font-family: ${value};">${selectedText || 'text'}</span>`;
        break;
      case 'fontSize':
        if (!value) return;
        const sizeMap = { '1': '10px', '2': '13px', '3': '16px', '4': '18px', '5': '24px', '6': '32px', '7': '48px' };
        replacement = `<span style="font-size: ${sizeMap[value] || '16px'};">${selectedText || 'text'}</span>`;
        break;
      case 'bold':
        replacement = `<strong>${selectedText || 'bold text'}</strong>`;
        break;
      case 'italic':
        replacement = `<em>${selectedText || 'italic text'}</em>`;
        break;
      case 'underline':
        replacement = `<u>${selectedText || 'underlined text'}</u>`;
        break;
      case 'h2':
        replacement = `\n<h2>${selectedText || 'Heading 2'}</h2>\n`;
        break;
      case 'h3':
        replacement = `\n<h3>${selectedText || 'Heading 3'}</h3>\n`;
        break;
      case 'blockquote':
        replacement = `\n<blockquote>\n  "${selectedText || 'Enlightening quote'}"\n</blockquote>\n`;
        break;
      case 'ul':
        replacement = `\n<ul>\n  <li>${selectedText || 'First item'}</li>\n  <li>Second item</li>\n</ul>\n`;
        break;
      case 'ol':
        replacement = `\n<ol>\n  <li>${selectedText || 'Step one'}</li>\n  <li>Step two</li>\n</ol>\n`;
        break;
      case 'link':
        const linkUrl = window.prompt('Enter website link URL:', 'https://');
        if (linkUrl) {
          replacement = `<a href="${linkUrl}" class="text-brand-green-medium hover:underline font-semibold" target="_blank">${selectedText || 'link text'}</a>`;
        } else {
          return;
        }
        break;
      case 'image':
        const url = window.prompt('Enter image URL:', 'https://images.unsplash.com/photo-1447752875215-b2761acb3c5d?auto=format&fit=crop&q=80&w=800');
        if (url) {
          replacement = `\n<img src="${url}" alt="Scenic Nature" class="w-full h-auto rounded-xl my-6" referrerPolicy="no-referrer" />\n`;
        } else {
          return;
        }
        break;
      case 'imageUpload':
        if (!value) return;
        replacement = `\n<img src="${value}" alt="Uploaded Image" class="w-full h-auto rounded-xl my-6" />\n`;
        break;
      default:
        return;
    }

    const newContent = text.substring(0, start) + replacement + text.substring(end);
    setContent(newContent);

    setTimeout(() => {
      textarea.focus();
      const offset = replacement.length - selectedText.length;
      textarea.setSelectionRange(start, end + offset);
    }, 50);
  };

  const handleInsertSampleTemplate = () => {
    const template = `
<p class="lead">Begin your article with a powerful, engaging paragraph (the lead statement) outlining the core concept or natural balance you are introducing to the reader.</p>

<h2>Establishing the Energetic Foundation</h2>
<p>In this section, write about your core discovery, the physical elements of the land, or the specific ecological restoration process. Use descriptive language that invokes a sense of touch, sight, and connection.</p>

<blockquote>
  "Nature does not hurry, yet everything is accomplished." — Lao Tzu
</blockquote>

<h2>Key Observations & Practical Steps</h2>
<p>Offer your readers practical suggestions they can implement on their properties immediately:</p>
<ul>
  <li><strong>Conduct a Bio-Assessment:</strong> Touch the soil, look at the foliage colors, and observe wildlife.</li>
  <li><strong>Minimize Interferences:</strong> Reduce heavy machinery compaction and chemical additives.</li>
  <li><strong>Grounding Practices:</strong> Dedicate physical spaces on your land for quiet gratitude and presence.</li>
</ul>

<h3>Synthesizing Natural Wisdom</h3>
<p>Conclude with a summary of the transformation, encouraging long-term land guardianship and mindful stewardship.</p>
    `;
    const newContent = content ? content + '\n' + template : template;
    setContent(newContent);
    if (editorMode === 'visual' && visualEditorRef.current) {
      visualEditorRef.current.innerHTML = newContent;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      alert('Please enter a blog title');
      return;
    }

    const tags = tagsInput
      .split(',')
      .map(t => t.trim())
      .filter(t => t.length > 0);

    setIsSaving(true);
    try {
      await onSave({
        _id: post?._id,
        title: title.trim(),
        subtitle: subtitle.trim(),
        content: content.trim() || '<p>Start writing your post here...</p>',
        coverImage: coverImage.trim(),
        tags: tags.length > 0 ? tags : ['General'],
        status,
        readTime
      });
      setIsSavedNotify(true);
      setTimeout(() => setIsSavedNotify(false), 2000);
    } catch (error) {
      console.error('Error saving post:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto py-8 px-4 md:px-8" id="blog-editor-root">
      <div className="flex items-center justify-between mb-8 pb-4 border-b border-gray-100">
        <button
          onClick={onCancel}
          className="flex items-center gap-2 text-gray-500 hover:text-brand-green-dark transition-colors font-medium text-sm cursor-pointer"
          id="btn-back"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Blogs
        </button>
        <h2 className="font-serif text-2xl md:text-3xl text-brand-green-dark font-medium">
          {post ? 'Edit Blog Post' : 'Compose New Blog Post'}
        </h2>
        <div className="w-24 hidden md:block"></div> 
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-4 flex flex-col gap-6">
          <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm flex flex-col gap-5">
            <h3 className="font-serif text-lg text-brand-green-dark font-semibold pb-3 border-b border-gray-50">
              Post Settings
            </h3>

            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                Publication Status
              </label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button
                  type="button"
                  id="status-draft"
                  onClick={() => setStatus('draft')}
                  className={`py-2.5 px-4 rounded-xl text-xs font-medium uppercase tracking-wider transition-all border ${
                    status === 'draft'
                      ? 'bg-brand-gold/10 border-brand-gold text-brand-gold-dark font-bold'
                      : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  Draft
                </button>
                <button
                  type="button"
                  id="status-published"
                  onClick={() => setStatus('published')}
                  className={`py-2.5 px-4 rounded-xl text-xs font-medium uppercase tracking-wider transition-all border ${
                    status === 'published'
                      ? 'bg-brand-green-light/10 border-brand-green-light text-brand-green-dark font-bold'
                      : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  Published
                </button>
              </div>
            </div>

            <div className="bg-brand-cream/40 p-3.5 rounded-xl border border-brand-cream flex items-center justify-between">
              <span className="text-xs text-brand-green-medium font-medium">
                Calculated Read Time:
              </span>
              <span className="text-xs font-mono font-bold text-brand-gold-dark bg-white py-1 px-2.5 rounded-md border border-gray-100">
                {readTime}
              </span>
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="tags" className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                Tags (Comma Separated)
              </label>
              <input
                id="tags"
                type="text"
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                placeholder="Land Healing, Process, Soil Science"
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-brand-gold focus:outline-none bg-brand-cream-light/30"
              />
              <span className="text-[11px] text-gray-400 italic">
                Add categories like: Process, Land Healing, Bio, Services.
              </span>
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="cover-image" className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                Cover Image URL
              </label>
              <div className="flex gap-2">
                <input
                  id="cover-image"
                  type="text"
                  value={coverImage}
                  onChange={(e) => setCoverImage(e.target.value)}
                  placeholder="https://images.unsplash.com/..."
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-brand-gold focus:outline-none bg-brand-cream-light/30"
                />
                <button
                  type="button"
                  onClick={() => coverImageInputRef.current?.click()}
                  className="px-4 py-2 bg-brand-green-medium hover:bg-brand-green-dark text-white rounded-xl text-xs font-semibold uppercase tracking-wider transition-all cursor-pointer whitespace-nowrap"
                >
                  Upload
                </button>
                <input 
                  type="file" 
                  accept="image/*" 
                  ref={coverImageInputRef} 
                  onChange={handleCoverImageUpload} 
                  className="hidden" 
                />
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                Or Select Premium Presets
              </span>
              <div className="grid grid-cols-5 gap-1.5 mt-1">
                {PRESET_IMAGES.map((preset) => {
                  const isSelected = coverImage === preset.url;
                  return (
                    <button
                      key={preset.name}
                      type="button"
                      title={preset.name}
                      onClick={() => setCoverImage(preset.url)}
                      className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                        isSelected ? 'border-brand-gold scale-95 shadow-sm' : 'border-transparent opacity-70 hover:opacity-100'
                      }`}
                    >
                      <img src={preset.url} alt="" className="w-full h-full object-cover" />
                      {isSelected && (
                        <div className="absolute inset-0 bg-brand-gold/20 flex items-center justify-center">
                          <Check className="w-4 h-4 text-white drop-shadow-md" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="bg-brand-green-dark text-brand-cream p-6 rounded-2xl border border-brand-green-light relative overflow-hidden">
            <div className="absolute -right-8 -bottom-8 w-24 h-24 rounded-full bg-brand-gold/10 border border-brand-gold/10"></div>
            <h4 className="font-serif text-base text-brand-gold font-medium mb-3 flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              Formatting Tips
            </h4>
            <ul className="text-xs text-brand-cream/80 space-y-2.5 list-disc pl-4 font-light">
              <li>Use <strong>Header 2</strong> (<code>&lt;h2&gt;</code>) for main article headings.</li>
              <li>Use <strong>Header 3</strong> (<code>&lt;h3&gt;</code>) for sub-topics.</li>
              <li>A <strong>Lead Paragraph</strong> is styled using <code>&lt;p class="lead"&gt;</code> for premium editorial typography.</li>
              <li>Create dynamic blockquotes using the <strong>Quote</strong> tool to format key quotes beautifully.</li>
            </ul>
          </div>
        </div>

        <div className="lg:col-span-8 flex flex-col gap-6">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm flex flex-col h-full overflow-hidden">
            <div className="p-6 md:p-8 border-b border-gray-50 flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <input
                  id="editor-title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Post Title (e.g. Balancing the Soils Resonance)"
                  className="w-full text-2xl md:text-3xl font-serif text-brand-green-dark placeholder-gray-300 border-b border-transparent hover:border-gray-100 focus:border-brand-gold focus:outline-none pb-2 transition-all"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <textarea
                  id="editor-subtitle"
                  value={subtitle}
                  onChange={(e) => setSubtitle(e.target.value)}
                  placeholder="Short, evocative subtitle or summary of this article..."
                  rows={2}
                  className="w-full text-sm text-gray-500 placeholder-gray-300 resize-none focus:outline-none border-b border-transparent hover:border-gray-100 focus:border-brand-gold pb-1 transition-all"
                />
              </div>
            </div>            
            <div className="bg-gray-50/80 px-6 py-3 border-b border-gray-100 flex flex-col md:flex-row gap-4 items-center justify-between">
              <div className="flex flex-wrap items-center gap-1.5 bg-white p-1 rounded-xl border border-gray-200">
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      handleFormat('fontName', e.target.value);
                      e.target.value = ''; // Reset after selection
                    }
                  }}
                  className="p-1 text-xs text-gray-500 hover:text-brand-green-dark bg-transparent border-none focus:outline-none cursor-pointer"
                  title="Font Family"
                >
                  <option value="">Font</option>
                  <option value="Arial">Arial</option>
                  <option value="Georgia">Georgia</option>
                  <option value="Times New Roman">Times New Roman</option>
                  <option value="Verdana">Verdana</option>
                  <option value="Courier New">Courier New</option>
                  <option value="Tahoma">Tahoma</option>
                  <option value="Trebuchet MS">Trebuchet MS</option>
                  <option value="Impact">Impact</option>
                  <option value="Comic Sans MS">Comic Sans MS</option>
                  <option value="Helvetica">Helvetica</option>
                  <option value="Garamond">Garamond</option>
                </select>
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      handleFormat('fontSize', e.target.value);
                      e.target.value = ''; // Reset after selection
                    }
                  }}
                  className="p-1 text-xs text-gray-500 hover:text-brand-green-dark bg-transparent border-none focus:outline-none cursor-pointer"
                  title="Font Size"
                >
                  <option value="">Size</option>
                  <option value="1">10px</option>
                  <option value="2">13px</option>
                  <option value="3">16px</option>
                  <option value="4">18px</option>
                  <option value="5">24px</option>
                  <option value="6">32px</option>
                  <option value="7">48px</option>
                </select>
                <div className="w-[1px] h-5 bg-gray-200 mx-1"></div>
                <button
                  type="button"
                  onClick={() => handleFormat('bold')}
                  className="p-2 text-gray-500 hover:text-brand-green-dark hover:bg-brand-cream-light rounded-lg transition-colors cursor-pointer"
                  title="Bold Text"
                >
                  <Bold className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => handleFormat('italic')}
                  className="p-2 text-gray-500 hover:text-brand-green-dark hover:bg-brand-cream-light rounded-lg transition-colors cursor-pointer"
                  title="Italic Text"
                >
                  <Italic className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => handleFormat('underline')}
                  className="p-2 text-gray-500 hover:text-brand-green-dark hover:bg-brand-cream-light rounded-lg transition-colors cursor-pointer"
                  title="Underline Text"
                >
                  <Underline className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => handleFormat('link')}
                  className="p-2 text-gray-500 hover:text-brand-green-dark hover:bg-brand-cream-light rounded-lg transition-colors cursor-pointer"
                  title="Insert Hyperlink"
                >
                  <Link className="w-4 h-4" />
                </button>
                <div className="w-[1px] h-5 bg-gray-200 mx-1"></div>
                <button
                  type="button"
                  onClick={() => handleFormat('h2')}
                  className="p-2 text-gray-500 hover:text-brand-green-dark hover:bg-brand-cream-light rounded-lg transition-colors cursor-pointer"
                  title="Heading 2"
                >
                  <Heading2 className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => handleFormat('h3')}
                  className="p-2 text-gray-500 hover:text-brand-green-dark hover:bg-brand-cream-light rounded-lg transition-colors cursor-pointer"
                  title="Heading 3"
                >
                  <Heading3 className="w-4 h-4" />
                </button>
                <div className="w-[1px] h-5 bg-gray-200 mx-1"></div>
                <button
                  type="button"
                  onClick={() => handleFormat('blockquote')}
                  className="p-2 text-gray-500 hover:text-brand-green-dark hover:bg-brand-cream-light rounded-lg transition-colors cursor-pointer"
                  title="Quote Block"
                >
                  <Quote className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => handleFormat('ul')}
                  className="p-2 text-gray-500 hover:text-brand-green-dark hover:bg-brand-cream-light rounded-lg transition-colors cursor-pointer"
                  title="Bullet List"
                >
                  <List className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => handleFormat('ol')}
                  className="p-2 text-gray-500 hover:text-brand-green-dark hover:bg-brand-cream-light rounded-lg transition-colors cursor-pointer"
                  title="Numbered List"
                >
                  <ListOrdered className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => handleFormat('image')}
                  className="p-2 text-gray-500 hover:text-brand-green-dark hover:bg-brand-cream-light rounded-lg transition-colors cursor-pointer"
                  title="Insert Image URL"
                >
                  <Image className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => handleFormat('uploadImage')}
                  className="flex items-center gap-1 p-2 text-gray-500 hover:text-brand-green-dark hover:bg-brand-cream-light rounded-lg transition-colors cursor-pointer text-xs font-medium"
                  title="Upload Image"
                >
                  Upload
                </button>
                <input 
                  type="file" 
                  accept="image/*" 
                  ref={inlineImageInputRef} 
                  onChange={handleInlineImageUpload} 
                  className="hidden" 
                />
                <div className="w-[1px] h-5 bg-gray-200 mx-1"></div>
                <button
                  type="button"
                  onClick={handleInsertSampleTemplate}
                  className="flex items-center gap-1 px-2.5 py-1 text-[11px] text-brand-gold-dark hover:text-brand-gold bg-brand-gold/5 hover:bg-brand-gold/10 border border-brand-gold/15 rounded-lg transition-all cursor-pointer font-medium"
                  title="Inject formatted template structure"
                >
                  <Sparkles className="w-3 h-3" />
                  Load Template
                </button>
              </div>

              <div className="flex items-center gap-1.5 bg-white p-1 rounded-xl border border-gray-200">
                <button
                  type="button"
                  onClick={() => setEditorTab('edit')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold tracking-wide transition-all uppercase cursor-pointer ${
                    editorTab === 'edit'
                      ? 'bg-brand-green-dark text-white shadow-sm font-bold'
                      : 'text-gray-500 hover:text-brand-green-dark hover:bg-gray-50'
                  }`}
                >
                  <Edit className="w-3.5 h-3.5" />
                  Write
                </button>
                <button
                  type="button"
                  onClick={() => setEditorTab('preview')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold tracking-wide transition-all uppercase cursor-pointer ${
                    editorTab === 'preview'
                      ? 'bg-brand-green-dark text-white shadow-sm'
                      : 'text-gray-500 hover:text-brand-green-dark hover:bg-gray-50'
                  }`}
                >
                  <Eye className="w-3.5 h-3.5" />
                  Preview
                </button>
                <button
                  type="button"
                  onClick={() => setEditorTab('split')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold tracking-wide transition-all uppercase cursor-pointer hidden md:flex ${
                    editorTab === 'split'
                      ? 'bg-brand-green-dark text-white shadow-sm'
                      : 'text-gray-500 hover:text-brand-green-dark hover:bg-gray-50'
                  }`}
                >
                  <Columns className="w-3.5 h-3.5" />
                  Split
                </button>
              </div>
            </div>

            <div className="flex-grow min-h-[450px]">
              {editorTab === 'edit' && (
                <div className="flex flex-col h-full min-h-[450px]">
                  <div className="flex items-center justify-between px-6 py-2 border-b border-gray-100 bg-gray-50/55">
                    <span className="text-xs font-semibold text-brand-green-light flex items-center gap-1 select-none">
                      {editorMode === 'visual' ? (
                        <>✍️ Visual Formatting (Google Docs Mode)</>
                      ) : (
                        <>💻 Advanced Source Code (HTML)</>
                      )}
                    </span>
                    <div className="flex bg-white rounded-lg p-0.5 border border-gray-200 text-[11px]">
                      <button
                        type="button"
                        onClick={() => setEditorMode('visual')}
                        className={`px-2.5 py-1 rounded-md font-medium transition-all cursor-pointer ${
                          editorMode === 'visual'
                            ? 'bg-brand-cream text-brand-green-dark font-bold'
                            : 'text-gray-400 hover:text-brand-green-medium'
                        }`}
                      >
                        Visual Formatter
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditorMode('code')}
                        className={`px-2.5 py-1 rounded-md font-medium transition-all cursor-pointer ${
                          editorMode === 'code'
                            ? 'bg-brand-cream text-brand-green-dark font-bold'
                            : 'text-gray-400 hover:text-brand-green-medium'
                        }`}
                      >
                        HTML Code
                      </button>
                    </div>
                  </div>

                  {editorMode === 'visual' ? (
                    <div 
                      className="relative overflow-y-auto w-full min-h-[450px] bg-white" 
                      onMouseLeave={() => setHoveredImgInfo(null)}
                    >
                      <div
                        id="editor-visual"
                        ref={visualEditorRef}
                        contentEditable
                        onInput={handleVisualInput}
                        onMouseOver={(e) => {
                          if (e.target.tagName === 'IMG') {
                            setHoveredImgInfo({
                              element: e.target,
                              top: e.target.offsetTop,
                              left: e.target.offsetLeft,
                              width: e.target.offsetWidth,
                              height: e.target.offsetHeight,
                            });
                          }
                        }}
                        placeholder="Start typing your beautiful blog article here... Use the toolbar above to style your headings, blockquotes, lists, and images!"
                        className="p-6 md:p-8 focus:outline-none prose max-w-none focus:ring-0 min-h-full outline-none"
                      />
                      {hoveredImgInfo && (
                        <div
                          className="absolute flex items-center justify-center bg-black/40 transition-opacity z-10"
                          style={{
                            top: hoveredImgInfo.top,
                            left: hoveredImgInfo.left,
                            width: hoveredImgInfo.width,
                            height: hoveredImgInfo.height,
                          }}
                          onMouseLeave={() => setHoveredImgInfo(null)}
                          onClick={() => {
                            hoveredImgInfo.element.remove();
                            handleVisualInput();
                            setHoveredImgInfo(null);
                          }}
                        >
                          <div className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-semibold shadow-lg flex items-center gap-2 cursor-pointer pointer-events-auto transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <textarea
                      id="editor-textarea"
                      ref={textareaRef}
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      placeholder="<p class='lead'>Start with an introduction...</p><h2>My Heading</h2><p>Your beautiful content...</p>"
                      className="w-full h-full min-h-[450px] p-6 md:p-8 font-mono text-sm leading-relaxed text-gray-700 bg-white focus:outline-none resize-none"
                    />
                  )}
                </div>
              )}

              {editorTab === 'preview' && (
                <div className="p-6 md:p-8 bg-brand-cream-light/40 min-h-[450px] overflow-y-auto max-h-[600px]">
                  {coverImage && (
                    <div className="w-full h-56 rounded-xl overflow-hidden mb-6 border border-gray-100 shadow-sm">
                      <img src={coverImage} alt="Post Cover" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <h1 className="font-serif text-3xl md:text-4xl text-brand-green-dark font-medium leading-tight mb-3">
                    {title || 'Untitled Post'}
                  </h1>
                  <p className="text-gray-500 text-base italic mb-6">
                    {subtitle || 'No subtitle provided yet.'}
                  </p>
                  
                  <div 
                    className="prose"
                    dangerouslySetInnerHTML={{ __html: content || '<p class="text-gray-400 italic">No content written yet. Click the "Write" tab above to start composing.</p>' }}
                  />
                </div>
              )}

              {editorTab === 'split' && (
                <div className="grid grid-cols-2 h-full min-h-[450px] divide-x divide-gray-100">
                  <textarea
                    ref={textareaRef}
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="Compose content in HTML format..."
                    className="w-full h-full min-h-[450px] p-6 font-mono text-xs leading-relaxed text-gray-700 focus:outline-none resize-none bg-white"
                  />
                  <div className="p-6 overflow-y-auto max-h-[500px] bg-brand-cream-light/30">
                    <h1 className="font-serif text-xl md:text-2xl text-brand-green-dark font-medium leading-tight mb-2">
                      {title || 'Untitled Post'}
                    </h1>
                    <div 
                      className="prose text-sm"
                      dangerouslySetInnerHTML={{ __html: content || '<p class="text-gray-400 italic">Type content in the editor to see real-time formatting...</p>' }}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="bg-gray-50 px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3">
              <button
                type="button"
                id="btn-cancel-post"
                onClick={onCancel}
                className="px-5 py-2.5 rounded-xl text-xs font-semibold uppercase tracking-wider text-gray-500 hover:text-brand-green-dark bg-white border border-gray-200 hover:bg-gray-50 cursor-pointer transition-all"
              >
                Cancel
              </button>
              <button
                type="submit"
                id="btn-save-post"
                disabled={isSaving}
                className={`flex items-center gap-2 text-white px-6 py-2.5 rounded-xl text-xs font-semibold uppercase tracking-wider transition-all shadow-sm border border-brand-green-dark/30 ${
                  isSaving ? 'bg-brand-green-dark opacity-70 cursor-not-allowed' : 'bg-brand-green-medium hover:bg-brand-green-dark cursor-pointer'
                }`}
              >
                {isSaving ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-3.5 h-3.5" />
                    {post ? 'Save Updates' : 'Publish Article'}
                  </>
                )}
              </button>
            </div>

          </div>
        </div>
      </form>
    </div>
  );
}
